import { useState, useEffect } from "react";
import { useCountryStore } from "../../store/countryStore.js";
import { useUserStore } from "../../store/userStore.js";
import dayjs from "dayjs";


import { genderItems } from "../../utils/arrayitems";

import { WhiteButton, PrimaryButton } from "../button";
import EditProfileBoxLabel from "../label/editprofileboxlabel";
import EditProfileInput from "../inputbox/editprofileinput";
import EditProfileDropdown from "../dropdown/editprofiledropdown";
import EditProfileDatepicker from "../datepicker/editprofiledatepicker";
import EditProfilePhoneInput from "../phoneinput/editprofilephoneinput";

import Loader from "../loader/loader";
import Spacer from "../spacer/spacer";
import ErrorText from "../errortext/errortext";

import images from "../../assets";

export default function UserBasicInfo() {
  const { countries, states, getStates } = useCountryStore();
  const { user, isUserLoading, getUser, updateUser } = useUserStore();

  const [state, setState] = useState({
    email: "",
    firstName: "",
    lastName: "",
    gender: "",
    dateOfBirth: "",
    phone: "",
    country: "",
    stateProvince: "",
  });
  const [error, setError] = useState({
    name: "",
    email: "",
    gender: "",
    dateOfBirth: "",
    phone: "",
    country: "",
    stateProvince: "",
  });

  useEffect(() => {
    const payload = { country: [state?.country] };
    getStates(payload);
  }, [getStates, state?.country]);

  useEffect(() => {
    setState({
      ...state,
      email: user?.email ?? "",
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      gender: user?.gender ?? "",
      dateOfBirth: user?.dateOfBirth ?? "",
      phone: user?.phone ?? "",
      country: user?.country ?? "",
      stateProvince: user?.stateProvince ?? "",
    });
  }, [user]);

  const getprofileData = () => {
    const payload = { username: user?.username };
    getUser(payload);
  };

  const handleSubmit = () => {
    if (!state?.firstName || !state?.lastName) {
      setError({
        ...error,
        name: "Name cannot be empty",
      });
    } else if (!state?.email) {
      setError({
        ...error,
        stateProvince: "Email cannot be empty",
      });
    } else if (!state?.phone) {
      setError({
        ...error,
        stateProvince: "Phone cannot be empty",
      });
    } else if (!state?.dateOfBirth) {
      setError({
        ...error,
        stateProvince: "Date of Birth cannot be empty",
      });
    } else if (!state?.gender) {
      setError({
        ...error,
        stateProvince: "Gender cannot be empty",
      });
    } else if (!state?.country) {
      setError({
        ...error,
        stateProvince: "Country cannot be empty",
      });
    } else if (!state?.stateProvince) {
      setError({
        ...error,
        stateProvince: "State cannot be empty",
      });
    } else {
      const payload = { username: user?.username, data: state };
      updateUser(payload, () => {
        getprofileData();
      });
    }
  };

  return isUserLoading ? (
    <Loader />
  ) : (
    <>
      <div className="w-full px-5 py-8 bg-white rounded-tl-2xl rounded-tr-2xl flex-col justify-start items-start gap-4 inline-flex">
        {/* name */}
        <div className="edit-profile-name-container">
          <div className="edit-profile-name">
            <EditProfileBoxLabel label="First Name" />
            <Spacer height="8px" />
            <EditProfileInput
              placeholder="First name"
              onChange={(e) =>
                setState({ ...state, firstName: e.target.value })
              }
              value={state.firstName || undefined}
            />
          </div>
          <div className="edit-profile-name">
            <EditProfileBoxLabel label="Last Name" />
            <Spacer height="8px" />
            <EditProfileInput
              placeholder="Last name"
              onChange={(e) => setState({ ...state, lastName: e.target.value })}
              value={state.lastName || undefined}
            />
          </div>
        </div>
        {error?.name && <ErrorText text={error?.name} />}
        {/* email */}
        <div className="edit-profile-label-container">
          <EditProfileBoxLabel label="Email" required={true} />
        </div>
        <div className="w-full">
          <EditProfileInput
            placeholder="Email"
            onChange={(e) => {
              setState({ ...state, email: e.target.value });
              setError({ ...error, email: "" });
            }}
            value={state.email || undefined}
          />
        </div>
        {error?.email && <ErrorText text={error?.email} />}

        {/* phone */}
        <div className="edit-profile-label-container">
          <EditProfileBoxLabel label="Phone" required={true} />
        </div>
        <div className="w-full">
          <EditProfilePhoneInput
            placeholder="Please enter your phone number"
            onChange={(e) => {
              setState({ ...state, phone: e });
              setError({ ...error, phone: "" });
            }}
            value={state.phone || undefined}
          />
        </div>
        {error?.phone && <ErrorText text={error?.phone} />}

        {/* date of birth */}
        <div className="edit-profile-label-container">
          <EditProfileBoxLabel label="Date of Birth" required={true} />
        </div>
        <div className="w-full">
          <EditProfileDatepicker
            placeholder="Select Date"
            onChange={(date, dateString) => {
              setState({ ...state, dateOfBirth: dateString });
              setError({ ...error, dateOfBirth: "" });
            }}
            value={
              state?.dateOfBirth
                ? dayjs(state?.dateOfBirth, "YYYY-MM-DD")
                : null
            }
          />
        </div>
        {error?.dateOfBirth && <ErrorText text={error?.dateOfBirth} />}

        {/* gender */}
        <div className="edit-profile-label-container">
          <EditProfileBoxLabel label="I am a" required={true} />
        </div>
        <div className="w-full">
          <EditProfileDropdown
            placeholder="Gender"
            data={genderItems}
            onChange={(e) => {
              setState({ ...state, gender: e });
              setError({ ...error, gender: "" });
            }}
            value={state.gender || undefined}
          />
        </div>
        {error?.gender && <ErrorText text={error?.gender} />}
        {/* country */}
        <div className="edit-profile-label-container">
          <EditProfileBoxLabel label="Country" required={true} />
        </div>
        <div className="w-full">
          <EditProfileDropdown
            placeholder="Please select"
            data={countries}
            list="country"
            onChange={(e) => {
              const country = JSON.parse(e);
              setState({ ...state, country: country.name });
              setError({ ...error, country: "" });
            }}
            value={state.country || undefined}
          />
        </div>
        {error?.country && <ErrorText text={error?.country} />}
        {/* state */}
        <div className="edit-profile-label-container">
          <EditProfileBoxLabel label="State/Province" required={true} />
        </div>
        <div className="w-full">
          <EditProfileDropdown
            placeholder="Please select"
            data={states}
            list="stateProvince"
            onChange={(e) => {
              setState({ ...state, stateProvince: e });
              setError({ ...error, stateProvince: "" });
            }}
            value={state.stateProvince || undefined}
          />
        </div>
        {error?.stateProvince && <ErrorText text={error?.stateProvince} />}
      </div>
      <div className="px-4 py-6 rounded-bl-2xl rounded-br-2xl flex-col justify-center items-end gap-2.5 inline-flex">
        <div className="self-stretch justify-between items-center inline-flex">
          <div className="justify-center items-center gap-3 flex">
            <PrimaryButton
              style={{
                width: "215px",
                fontSize: "14px",
              }}
              text={"Change Password"}
              iconalign="left"
              icon={images.lockIcon}
              disabled={isUserLoading}
            />
          </div>
          <div className="items-center gap-[25px] flex">
            <PrimaryButton
              style={{
                width: "157px",
              }}
              text="Save"
              disabled={isUserLoading}
              onClick={handleSubmit}
            />
            <WhiteButton
              style={{
                width: "157px",
              }}
              text="Cancel"
              disabled={isUserLoading}
              onClick={getprofileData}
            />
          </div>
        </div>
      </div>
    </>
  );
}








